<template>
    <v-chart class="chart" ref="chartRef" :option="chartOption" @click="handleChartClick" />
  </template>
  
  <script setup>
  import { ref, watch, onMounted, defineProps, computed } from 'vue';
  import { useStore } from 'vuex'
  import { useI18n } from 'vue-i18n';
  import { use } from "echarts/core";
  import { CanvasRenderer } from "echarts/renderers";
  import { BarChart } from "echarts/charts";
  import { TooltipComponent, TitleComponent, GridComponent, LegendComponent } from "echarts/components";
  import VChart from "vue-echarts";
  
  use([CanvasRenderer, BarChart, TooltipComponent, TitleComponent, GridComponent, LegendComponent]);

  const store = useStore();
  const { t } = useI18n();
  
  const props = defineProps({
    documents: {
      type: Array,
      required: true
    },
    teamsObj: {
      type: Object,
      required: true
    },
    usersObj: {
      type: Object,
      required: true
    }
  });

  
  
  const chartRef = ref(null);
  const chartOption = ref({});
  const isDrilldown = ref(false);
  const currentTeam = ref(null);
  const chartColor = computed(() => store.getters.chartFontColor);
  
  const processedData = computed(() => {
    const teamData = {};
  
    props.documents.forEach(doc => {
      if (doc.createdByTeamId) {
        const teamName = props.teamsObj[doc.createdByTeamId]?.name || 'Unknown Team';
        teamData[teamName] = teamData[teamName] || { total: 0, users: {}, id: doc.createdByTeamId };
        teamData[teamName].total += 1;
  
        if (doc.createdByUserId) {
          const userName = props.usersObj[doc.createdByUserId]?.displayName || 'Unknown User';
          teamData[teamName].users[doc.createdByUserId] = teamData[teamName].users[doc.createdByUserId] || { name: userName, value: 0 };
          teamData[teamName].users[doc.createdByUserId].value += 1;
        }
      }
    });
  
    return {
      teamData: Object.entries(teamData).map(([name, data]) => ({ name, value: data.total, users: data.users, id: data.id })),
    };
  });
  
  const updateChartOption = () => {
    const { teamData } = processedData.value;
  
    if (isDrilldown.value && currentTeam.value) {
      const team = teamData.find(team => team.name === currentTeam.value);
      const userData = Object.values(team.users);
      const sortedUserData = userData.sort((a, b) => a.value - b.value);
  
      chartOption.value = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          confine: true,
          enterable: true,
          extraCssText: 'max-width: 320px; overflow-x: auto;'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          boundaryGap: [0, 0.01],
          axisLabel: {
            color: chartColor.value
          }
        },
        yAxis: {
          type: 'category',
          data: sortedUserData.map(item => item.name),
          axisLabel: {
            color: chartColor.value
          }
        },
        series: [
          {
            name: 'Users',
            type: 'bar',
            data: sortedUserData.map(item => item.value),
            itemStyle: {
              color: '#91cc75'
            }
          }
        ]
      };
    } else {
      const sortedTeamData = teamData.sort((a, b) => a.value - b.value);
  
      chartOption.value = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          confine: true,
          enterable: true,
          extraCssText: 'max-width: 320px; overflow-x: auto;'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          boundaryGap: [0, 0.01],
          axisLabel: {
            color: chartColor.value
          }
        },
        yAxis: {
          type: 'category',
          data: sortedTeamData.map(item => item.name),
          axisLabel: {
            color: chartColor.value
          }
        },
        series: [
          {
            name: t('teams'),
            type: 'bar',
            data: sortedTeamData.map(item => item.value),
            itemStyle: {
              color: '#5470c6',
            }
          }
        ]
      };
    }
  };
  
  const handleChartClick = (params) => {
    if (!isDrilldown.value) {
      isDrilldown.value = true;
      currentTeam.value = params.name;
      updateChartOption();
    } else {
      isDrilldown.value = false;
      currentTeam.value = null;
      updateChartOption();
    }
  };
  
  watch(() => props.documents, () => {
    updateChartOption();
  }, { deep: true });
  
  watch(() => props.teamsObj, () => {
    updateChartOption();
  }, { deep: true });
  
  watch(() => props.usersObj, () => {
    updateChartOption();
  }, { deep: true });

  watch(() => chartColor.value, () => {
    updateChartOption();
  });

  onMounted(() => {
    updateChartOption();
    window.addEventListener('resize', () => {
      if (chartRef.value) {
        chartRef.value.resize();
      }
    });
  });
  </script>
  
  <style scoped>
  .chart {
    height: 400px;
  }
  </style>